import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import MedenticAuthEntryPage from "@shared/ui/MedenticAuthEntryPage";
import { ErrorResponse } from "@shared/types/commonTypes";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Banner,
  BlockStack,
  Button,
  SkeletonBodyText,
  Text,
  Box,
  InlineStack,
  Avatar,
  Card,
} from "@medentic/polaris-react";
import Cookies from "js-cookie";
import { ROOTS, USER_ROUTES, ROUTES } from "@app/config/constants";
import { User } from "@shared/types/user";
import { clearTokenData } from "@features/auth/utils";
import dayjs from "dayjs";
import { useState } from "react";
import { TranslateLink } from "@shared/ui";
import { setLocalStorage } from "@shared/lib";

interface QESIssuer {
  id: string;
  name: string;
  website: string;
  logoSVG: string;
  country: string;
  serialNumber: string;
  organization: string;
  commonName: string;
}

interface QESConnectResponse {
  name: string;
  issuer: QESIssuer;
  validFrom: string;
  validTo: string;
  error?: ErrorResponse;
}

export const QESConnect: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { GET, POST } = useApiRequest();
  const [searchParams] = useSearchParams();
  const connectQesId = searchParams.get("key");
  const navigate = useNavigate();
  const accessToken = Cookies.get("access_token");
  const queryClient = useQueryClient();
  const [showBanner, setShowBanner] = useState(true);

  const user = queryClient.getQueryData<User>(["user"]);

  const isAuthenticated = !!accessToken;

  const { isLoading, error, data } = useQuery<
    QESConnectResponse,
    ErrorResponse
  >({
    queryKey: ["qes-connect", connectQesId],
    queryFn: () => GET(`/api/connect-app/connect-qes/${connectQesId}`),
    retry: false,
  });

  const errorMessage = error?.response?.data?.error?.display;

  const handleCloseWindow = () => {
    if (isAuthenticated) {
      navigate("/c");
    } else {
      navigate(ROUTES.LOGIN);
    }
  };

  const {
    mutate: connectQes,
    isPending: isConnecting,
    error: connectQesError,
  } = useMutation({
    mutationFn: () =>
      POST(`/api/user/me/qes/${connectQesId}/connect`, {
        keyId: connectQesId,
      }),
    onSuccess: () => {
      navigate(`${ROOTS.USER}/${USER_ROUTES.QES}`);
    },
  });

  const renderSkeleton = () => {
    return <SkeletonBodyText lines={4} />;
  };

  const renderLoggedInUser = (user: User) => {
    const { firstName, lastName, email, avatar } = user;

    return (
      <Card>
        <InlineStack align="space-between" blockAlign="center">
          <BlockStack gap="100">
            <Text variant="bodyMd" as="p" tone="subdued">
              {t("qesConnect.loggedInAs")}
            </Text>
            <InlineStack gap="300" blockAlign="center">
              <Avatar
                size={"lg"}
                name={`${firstName} ${lastName}`}
                initials={avatar.initials}
                source={avatar.image}
              />

              <BlockStack>
                <Text variant="bodyMd" as="p">
                  {firstName} {lastName}
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  {email}
                </Text>
              </BlockStack>
            </InlineStack>
          </BlockStack>
          <Button
            variant="plain"
            onClick={() => {
              clearTokenData();
            }}
          >
            {t("qesConnect.logout")}
          </Button>
        </InlineStack>
      </Card>
    );
  };

  const renderInValidKeyContent = () => {
    return (
      <BlockStack gap="400">
        <Text variant="headingLg" as="h1">
          {t("qesConnect.title")}
        </Text>

        {error && (
          <Banner tone="critical">
            {errorMessage ?? t("qesConnect.invalidKey")}
          </Banner>
        )}

        <Button onClick={handleCloseWindow} variant="secondary" fullWidth>
          {t("qesConnect.closeWindow")}
        </Button>
      </BlockStack>
    );
  };

  const renderValidKeyContent = () => {
    const qesData = data;

    const locale = i18n.language;
    const userTimezone = user?.timezone?.id ?? "UTC";

    dayjs.locale(locale);

    const formattedDate = dayjs
      .utc(qesData?.validTo)
      .tz(userTimezone)
      .format("LL");

    return (
      <BlockStack gap="500">
        {isAuthenticated && user
          ? renderLoggedInUser(user)
          : showBanner && (
              <Banner
                title={t("qesConnect.loginBanner.title")}
                tone="warning"
                action={{
                  content: t("qesConnect.loginBanner.loginButton"),
                  onAction: () => {
                    setLocalStorage(
                      "__REDIRECT_PATH",
                      location.pathname + location.search
                    );
                    navigate(ROUTES.LOGIN);
                  },
                }}
                secondaryAction={{
                  content: t("qesConnect.loginBanner.registerButton"),
                  onAction: () => {
                    setLocalStorage(
                      "__REDIRECT_PATH",
                      location.pathname + location.search
                    );
                    navigate(ROUTES.REGISTER);
                  },
                }}
                onDismiss={() => setShowBanner(false)}
              >
                {t("qesConnect.loginBanner.description")}
              </Banner>
            )}

        <Text variant="headingLg" as="h1">
          {t("qesConnect.title")}
        </Text>

        <Text as="p" variant="bodyMd" tone="subdued">
          {t("qesConnect.description")}
        </Text>

        <Box
          background="bg-surface"
          padding="500"
          borderRadius="300"
          borderColor="border"
          borderWidth="025"
        >
          <BlockStack gap="200">
            <InlineStack gap="300" blockAlign="center">
              <Box>
                <img
                  width={50}
                  height={50}
                  src={qesData?.issuer.logoSVG}
                  alt={qesData?.issuer.name}
                />
              </Box>
              <BlockStack gap="100">
                <Text variant="headingSm" as="h2">
                  {qesData?.name}
                </Text>
                <Text as="p" tone="subdued">
                  {t("qesConnect.qesDetails.validTill", {
                    date: formattedDate,
                  })}
                </Text>
                <Text as="p" tone="subdued">
                  {t("qesConnect.qesDetails.issuedBy", {
                    issuer: qesData?.issuer.name,
                  })}
                </Text>
              </BlockStack>
            </InlineStack>
          </BlockStack>
        </Box>

        <Button
          onClick={handleConfirmConnect}
          variant="primary"
          fullWidth
          disabled={!isAuthenticated || isConnecting}
          loading={isConnecting}
          size="large"
        >
          {isConnecting
            ? t("qesConnect.connectButton.connecting")
            : t("qesConnect.connectButton.confirm")}
        </Button>
        {connectQesError && (
          <Banner tone="critical">
            {(connectQesError as unknown as ErrorResponse)?.response?.data
              ?.error?.display || "An error occurred"}
          </Banner>
        )}

        <Box>
          <TranslateLink text={t("qesConnect.disclaimer.text")} />
        </Box>
      </BlockStack>
    );
  };

  const handleConfirmConnect = () => {
    if (isAuthenticated && connectQesId) {
      connectQes();
    }
  };

  return (
    <MedenticAuthEntryPage
      wrapperClass="white-card-wrapper"
      showHelpFooter
    >
      {isLoading
        ? renderSkeleton()
        : error
        ? renderInValidKeyContent()
        : renderValidKeyContent()}
      <br />
    </MedenticAuthEntryPage>
  );
};
